// The chef's Today (kitchen flow, Batch 4): what needs the kitchen now, and
// the next week of events, each with its five steps and ONE next step. One
// read (GET /inventory-flow/kitchen-today) for the kitchen tablet's weak
// wifi; live updates refetch it with the rest of the page.
// Rules: lib/inventoryFlow/todayLogic.ts. Strings: todayStrings.ts.
import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { friendlyError } from '@/lib/inventoryFlow/friendlyError'
import { pollInterval, type RequisitionStreamStatus } from '@/lib/inventoryFlow/requisitionStream'
import { Button } from '@/components/ui/button'
import { cn, formatDateHuman } from '@/lib/utils'
import { intentScope, useIntentKeys } from '@/lib/inventoryFlow/intentKey'
import type { QueuedAction } from '@/lib/inventoryFlow/offlineQueue'
import { T } from '@/lib/inventoryFlow/strings'
import {
  comingUp, daysBetween, fmtEventTime, needsYouNow, nextStep, planName, statusLine, stepInputOf, stepsDone, stockState, type NeedItem, type NextStep,
} from '@/lib/inventoryFlow/todayLogic'
import { agoText, statusText, TT } from '@/lib/inventoryFlow/todayStrings'
import type { KitchenToday, KitchenTodayPlan } from '@/lib/inventoryFlow/todayTypes'
import { useBasicsSend } from './BasicsTab'
import type { KitchenRun } from './RequisitionPanel'
import { StepBar } from './PlanSteps'

type Notify = (kind: 'success' | 'error', message: string) => void

export const TODAY_KEY = ['inventory-flow', 'kitchen-today'] as const

function errMessage(e: unknown): string {
  return friendlyError(e, { brief: true })
}

export function TodayTab({ notify, run, queue, onOpenPlan, onOpenRequest, onAllEvents, live = 'offline' }: {
  notify: Notify
  /** The live stream's state: a slow poll while live, the normal one when not. */
  live?: RequisitionStreamStatus
  run: KitchenRun
  queue: QueuedAction[]
  /** Opens the plan under Events; wrapUp opens its wrap-up straight away. */
  onOpenPlan: (planId: string, opts?: { wrapUp?: boolean }) => void
  onOpenRequest: (reqId: string) => void
  onAllEvents: () => void
}) {
  const { data, isLoading, isError, refetch, isFetching } = useQuery({ queryKey: TODAY_KEY, queryFn: () => apiClient.getKitchenToday(), refetchInterval: pollInterval(live) })
  const t = data?.data

  if (isLoading) return <p className="text-muted-foreground">{TT.loading}</p>
  if (isError || !t) {
    return (
      <div className="space-y-3 rounded-lg border p-4">
        <p className="text-muted-foreground">{TT.loadFailed}</p>
        <Button variant="outline" disabled={isFetching} onClick={() => void refetch()}>{TT.retry}</Button>
      </div>
    )
  }
  return (
    <div className="space-y-6">
      <NeedsYouNow t={t} notify={notify} run={run} queue={queue} onOpenPlan={onOpenPlan} onOpenRequest={onOpenRequest} />
      <ComingUp t={t} notify={notify} onOpenPlan={onOpenPlan} onOpenRequest={onOpenRequest} onAllEvents={onAllEvents} />
    </div>
  )
}

// ── Needs you now ───────────────────────────────────────────────────────────

function NeedsYouNow({ t, notify, run, queue, onOpenPlan, onOpenRequest }: {
  t: KitchenToday
  notify: Notify
  run: KitchenRun
  queue: QueuedAction[]
  onOpenPlan: (planId: string, opts?: { wrapUp?: boolean }) => void
  onOpenRequest: (reqId: string) => void
}) {
  const items = useMemo(() => needsYouNow(t), [t])
  const basics = items.find((i): i is Extract<NeedItem, { kind: 'basics' }> => i.kind === 'basics')
  const { send, waiting } = useBasicsSend({ side: 'kitchen', lines: basics?.lines ?? [], run, queue, notify })
  const now = new Date()
  const when = (d: string | null) => {
    if (!d) return ''
    const n = daysBetween(t.today, d)
    return n === 0 ? TT.dayToday.toLowerCase() : n === 1 ? TT.dayTomorrow.toLowerCase() : formatDateHuman(d)
  }
  return (
    <section className="space-y-3">
      <h2 className="text-base font-semibold">{TT.needsTitle}</h2>
      {items.length === 0 ? (
        <p className="rounded-lg border p-4 text-sm text-muted-foreground">{TT.needsNone}</p>
      ) : (
        <ul className="space-y-2">
          {items.map((n) => {
            switch (n.kind) {
              case 'arrival':
                return (
                  <NeedRow key={n.key} tone="info" title={TT.arrivalTitle}
                    detail={[n.planTitle ? TT.arrivalFor(n.planTitle, when(n.planDate)) : TT.arrivalReq(n.reqNo), `${n.items} item${n.items === 1 ? '' : 's'}`,
                      n.sentAt ? TT.sentAgo(agoText(n.sentAt, now, (d) => formatDateHuman(d)), n.sentBy) : ''].filter(Boolean).join(' · ')}>
                    <Button onClick={() => onOpenRequest(n.reqId)}>{TT.checkIn}</Button>
                  </NeedRow>
                )
              case 'short':
                return (
                  <NeedRow key={n.key} tone="danger" title={TT.shortTitle(n.reqNo)} detail={TT.shortHelp(n.items, n.planTitle)}>
                    <Button variant="outline" onClick={() => onOpenRequest(n.reqId)}>{TT.open}</Button>
                  </NeedRow>
                )
              case 'overdue':
                return (
                  <NeedRow key={n.key} tone="danger" title={TT.overdueTitle(n.name, n.days)} detail={TT.overdueHelp}>
                    <Button onClick={() => onOpenPlan(n.planId, { wrapUp: true })}>{TT.wrapUp}</Button>
                  </NeedRow>
                )
              case 'basics':
                return (
                  <NeedRow key={n.key} title={TT.basicsTitle(n.names.length)} detail={TT.basicsNames(n.names)}
                    note={waiting ? T.waitingToSend : undefined}>
                    <Button variant="outline" disabled={send.isPending || waiting} onClick={() => send.mutate()}>
                      {send.isPending ? TT.asking : TT.askForThem}
                    </Button>
                  </NeedRow>
                )
            }
          })}
        </ul>
      )}
    </section>
  )
}

function NeedRow({ tone, title, detail, note, children }: { tone?: 'info' | 'danger'; title: string; detail: string; note?: string; children: React.ReactNode }) {
  return (
    <li className={cn('flex flex-wrap items-center gap-3 rounded-lg border p-4',
      tone === 'info' && 'border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30',
      tone === 'danger' && 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30')}>
      <div className="min-w-0 flex-[1_1_18rem]">
        <p className="font-semibold">{title}</p>
        <p className="mt-0.5 text-sm text-muted-foreground">{detail}</p>
        {note && <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">{note}</p>}
      </div>
      {children}
    </li>
  )
}

// ── Coming up ───────────────────────────────────────────────────────────────

function ComingUp({ t, notify, onOpenPlan, onOpenRequest, onAllEvents }: {
  t: KitchenToday
  notify: Notify
  onOpenPlan: (planId: string, opts?: { wrapUp?: boolean }) => void
  onOpenRequest: (reqId: string) => void
  onAllEvents: () => void
}) {
  const plans = useMemo(() => comingUp(t), [t])
  const qc = useQueryClient()
  // "Start cooking" acts from here; one key per plan, reused on a retry.
  const keys = useIntentKeys()
  const start = useMutation({
    mutationFn: (id: string) => apiClient.productionPlanAction(id, 'start', undefined, keys.keyFor(intentScope('start', id))),
    onSuccess: (_res, id) => {
      keys.renew(intentScope('start', id))
      qc.invalidateQueries({ queryKey: ['inventory-flow'] })
      notify('success', TT.started)
    },
    onError: (e) => {
      qc.invalidateQueries({ queryKey: ['inventory-flow'] })
      notify('error', errMessage(e))
    },
  })
  const act = (p: KitchenTodayPlan, step: NextStep) => {
    if (step === 'start') return start.mutate(p.id)
    if (step === 'check-in') {
      const arrival = t.arrivals.find((a) => a.production_plan_id === p.id)
      const reqId = arrival?.id ?? p.requisition_id
      if (reqId) return onOpenRequest(reqId)
    }
    // Locking, sending (the amounts are checked on the plan), waiting and
    // wrapping up all happen on the plan itself.
    onOpenPlan(p.id, step === 'wrap-up' && p.status !== 'requisitioned' ? { wrapUp: true } : undefined)
  }

  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-base font-semibold">{TT.comingTitle}</h2>
        <Button variant="link" className="h-auto p-0" onClick={onAllEvents}>{TT.allEvents}</Button>
      </div>
      {plans.length === 0 ? (
        <p className="rounded-lg border p-4 text-sm text-muted-foreground">{TT.comingNone}</p>
      ) : (
        <ul className="divide-y rounded-lg border bg-card">
          {plans.map((p) => {
            const input = stepInputOf(p)
            const step = nextStep(input, t.today)
            const done = stepsDone(input)
            const stock = stockState(input)
            const arrival = t.arrivals.find((a) => a.production_plan_id === p.id)
            const line = statusLine(input, arrival?.items)
            const days = daysBetween(t.today, p.for_date)
            const time = fmtEventTime(p.booking?.event_time)
            const primary = step !== null && step !== 'wait' && step !== 'confirm'
            return (
              <li key={p.id} className="flex flex-wrap items-center gap-x-4 gap-y-3 p-4">
                <button type="button" className="min-w-0 flex-[1_1_18rem] text-left" onClick={() => onOpenPlan(p.id)}>
                  <span className="flex flex-wrap items-baseline gap-x-3">
                    <span className="font-semibold">{days === 0 ? TT.dayToday : days === 1 ? TT.dayTomorrow : formatDateHuman(p.for_date)}</span>
                    <span className="text-xs text-muted-foreground">{[days <= 1 ? formatDateHuman(p.for_date) : '', time].filter(Boolean).join(' · ')}</span>
                  </span>
                  <span className="mt-1 block font-semibold">{planName(p)}</span>
                  <span className="mt-0.5 block text-sm text-muted-foreground">
                    {[p.booking?.guest_count ? TT.guests(p.booking.guest_count) : TT.portions(p.portion_count), TT.dishes(p.dish_count),
                      p.order_type === 'takeaway' ? TT.packed : TT.served].join(' · ')}
                  </span>
                  <StepBar done={done} waiting={stock === 'waiting' || stock === 'partly'} />
                  <span className={cn('mt-1 block text-xs',
                    line.kind === 'here' || line.kind === 'nothingAsked' ? 'text-emerald-700 dark:text-emerald-300'
                      : line.kind === 'waiting' || line.kind === 'partly' || line.kind === 'arrived' ? 'text-amber-700 dark:text-amber-300'
                        : line.kind === 'refused' ? 'text-destructive' : 'text-muted-foreground')}>
                    {statusText(line)}
                  </span>
                </button>
                {step && (
                  <Button variant={primary ? 'default' : 'outline'} disabled={start.isPending && start.variables === p.id} onClick={() => act(p, step)}>
                    {TT.nextButton[step]}
                  </Button>
                )}
              </li>
            )
          })}
        </ul>
      )}
      <p className="text-xs text-muted-foreground">{TT.stepsHelp}</p>
    </section>
  )
}
